import React, { useState } from "react";
import { AddFilterBaseFunc, chartAttrs, defaultStyles, IDimFacility, IFilterKeys, IFilters, IPlotData, RemoveFilterBaseFunc } from "models/core";
import { DashboardFilterKeys } from "models/mdt";
import { PlotData } from "plotly.js";
import { Dialog } from "@mui/material";
import { DashChart } from "components/common/DashChart";
import { ChartIcons } from "components/common/ChartIcons";
import { chartDimesionsCalc } from "../core";
import watermark from "../../static/qca-black.png";
import { CreateChart } from "components/common/CreateChart";

interface IProps {
	plotData: {[facCancerKey: string]: { survivalTime: string, noMdtKMSurvival: number, stateKMSurvival: number }};
	mdtFacilityRef: IDimFacility[];
	activeFilters: IFilters<IFilterKeys>;
	addFilterFn: AddFilterBaseFunc<typeof DashboardFilterKeys>;
    removeFilterFn: RemoveFilterBaseFunc<typeof DashboardFilterKeys>;
}

/**
 * Reviews by service chart / My MDTs
 */
export function KMSurvivalChart(props: IProps) {
	const { plotData, mdtFacilityRef, activeFilters } = props;
	const [ chartType, setChartType ] = useState("scatter");
    const [ chartOrientation, setChartOrientation ] = useState("v");
	const [ fullScreenOpen, setFullScreenState ] = useState(false);
    
	const chartDimensions = chartDimesionsCalc(50, 12, 12);
	const chartTitle = "KM Survival";
	const xtitle = (chartOrientation == "h" ? "Survival Rate (%)" : "Years");
	const ytitle = (chartOrientation == "h" ? "Years" : "Survival Rate (%)");

	//Filter the survival data with the current mdt+Cancer filters
	//Get the MDT name from current filters
	const facName = activeFilters?.Facility?.value[0];

    //Get the cancer label based on current filters 
	let cancerName: string = activeFilters?.PrimarySite?.parts[0];
	//Check if at second level in hierarchy, if so add this also to the cancer name 
	if (activeFilters?.PrimarySite?.parts?.length == 2) {
		cancerName += activeFilters?.PrimarySite?.parts[1];
	}

	let mdtSurvival: IPlotData = {labels: [], values: []};
    let stateSurvival: IPlotData = {labels: [], values: []};

	//Only if there is only one MDT selected and only one cancer site selected 
	if (activeFilters?.Facility?.value.length == 1 && activeFilters?.PrimarySite?.value.length == 1) { 
        
		//Get MDTID from the MDT name
		let facId = mdtFacilityRef.find(x => x.name == facName)?.facilityId;
        const prefix = facId+cancerName+"|";

        //Extract labels (survival time)
        let labels: string[] = [];
        for (const key in plotData) {
            if (Object.prototype.hasOwnProperty.call(plotData, key)) {
                if (key.startsWith(prefix)) {
                    labels.push(key.substring((key.indexOf("|")+1)));
                }
            }
        }
        
		//Use labels to extract the values
        labels.forEach(a => {
			if (plotData[facId+cancerName+"|"+a]) {
                mdtSurvival.labels.push(a)
                mdtSurvival.values.push(plotData[facId+cancerName+"|"+a].noMdtKMSurvival)

                stateSurvival.labels.push(a)
                stateSurvival.values.push(plotData[facId+cancerName+"|"+a].stateKMSurvival)
			}
		})
    }

	const valuesMDT = mdtSurvival.values;
	const labelsMDT = mdtSurvival.labels;
	const mdt = {"name": "My Facility", "labels": labelsMDT, "values": valuesMDT};

    const valuesState = stateSurvival.values;
	const labelsState = stateSurvival.labels;
	const state = {"name": "Queensland", "labels": labelsState, "values": valuesState};

    const showChart = mdtSurvival.values.length > 0;

	const tracesArray: any[] = [];
	tracesArray.push(mdt);
    tracesArray.push(state);

	const traces: Partial<PlotData>[] = [];

    tracesArray?.forEach((a, i) => {    

		//Ensure labels are read in as numbers then sort in ascending order 
        let xs: number[] = a.labels.map(x => {
            return Number(x);
        })
        xs.sort((a,b)=>a-b);

		//Use the order of the labels to assign the values in the correct order 
        let ys: number[] = new Array(xs.length)
        for (var x=0; x<xs.length; x++) {
            ys[x] = a.values[a.labels.indexOf(xs[x].toString())];
          }

        //Get year and month labels instead of random decimal 
        let survivalLabel: string[] = [];
        
        xs.forEach(x => {
            let years = Math.floor(x);
            let months = Math.round((x % 1)*12)

            survivalLabel.push(years + " years, " + months + " months")
        }) 

        traces.push({
            name: a.name,
            mode: "text+lines",
            x: xs,
            y: ys,
            hovertext: survivalLabel.map(x => x),
            hovertemplate: "%{hovertext}<br>" +
                            "%{fullData.name}: %{y:,}%<br>" +
                            "<extra></extra>",
            type: chartType, 
            orientation: "v",
            marker: {},
        } as unknown as Plotly.PlotData);
    })

    //Custom styling: Change y axis range to limit to 100% and just show full year labels 
    const style = defaultStyles.defaultBox;
	const bx = chartAttrs(style.height);
    const styling = Object.assign({}, bx.layout)
	if (chartOrientation == "v") {
    	styling.yaxis = { range: [0, 110] };
	}

	if (chartOrientation == "h") {
    	styling.xaxis = { range: [0, 110] };
	}

    styling.xaxis = { automargin:true, tick: 0, autotick: false, dtick: 1, autotypenumbers: 'strict', title: "Years", 
                                        fixedrange: true, zeroline: false, rangemode: "tozero"
                    };


	//Full screen chart
	const logo = watermark;
	const  fullScreenChart =
		<Dialog
			open={fullScreenOpen || false}
			fullWidth={true}
			maxWidth={false}
			style={{width: (window.innerWidth - 100), 
					height: ((chartDimensions.fullScreenPaperHeight*1.3) + "px"),
					marginLeft: 50,
					marginRight: 50 }}
		>
				<DashChart style={{height: (chartDimensions.fullScreenPaperHeight) + "px"}} 
							heading ={chartTitle} 
							chartKey = {chartTitle+"fullScreen"}
						> 
					<div style={{marginTop: "-70px", float: "right", display: "inline"}}>
					<ChartIcons vbar ={false} hbar ={false}
						table={true}
						scatter={true}
						pie={false}
						downloadAsCsv={true}
						downloadAsPng={true}
						isFullScreen={fullScreenOpen}
						viewAsFullScreen={true}
						tooltipInfo={false}
						chartOrientation={chartOrientation}
						chartType={chartType}
						traces = {traces}
						xLabel = {(chartOrientation=="h" ? ytitle : xtitle)} 
						yLabel = {(chartOrientation=="h" ? xtitle : ytitle)}
						title= {chartTitle}
						setChartOrientation = {setChartOrientation}
						setChartType = {setChartType}
						setFullScreen = {setFullScreenState}
						currentFilters = {activeFilters}
						/>
					<br/>
					{<img
					src={logo}
					alt="Cancer Alliance Queensland"
					width={200}
					style={{marginTop: "20px"}}
						/>}
					</div>    
					{CreateChart(chartDimensions.fullScreenChartHeight * 1.2,
								 chartDimensions.fullScreenChartWidth,
								 chartDimensions.fullScreenPaperHeight,
								 chartOrientation,
								 chartType, 
								 showChart, 
								 xtitle, 
								 ytitle, 
								 mdtSurvival.labels,
								 mdtSurvival.values, 
								 traces,
                                 null,
                                 styling)}
				</DashChart>
		</Dialog>


	return (
		<div className={"paper"} key={chartTitle}
        style={{
            float: "right",
            width: "100%",
			padding: "12px",
        }}
        >  
	 	<ChartIcons vbar ={true} hbar ={true}
			table={true}
			scatter={true}
			pie={false}
			downloadAsCsv={true}
			downloadAsPng={true}
			isFullScreen={fullScreenOpen}
			viewAsFullScreen={true}
			tooltipInfo={false}
			chartOrientation={chartOrientation}
			chartType={chartType}
			traces = {traces}
			xLabel = {(chartOrientation=="h" ? ytitle : xtitle)} 
			yLabel = {(chartOrientation=="h" ? xtitle : ytitle)}
			title= {chartTitle}
			setChartOrientation = {setChartOrientation}
            setChartType = {setChartType}
			setFullScreen = {setFullScreenState}
			currentFilters = {activeFilters}
			/>
		<DashChart chartKey={chartTitle+"chart"} heading ={chartTitle} style ={{height: chartDimensions.paperHeight + "px", 
																										width: chartDimensions.paperWidth}}>
			{CreateChart(chartDimensions.chartHeight*1.05,
								 chartDimensions.chartWidth,
								 chartDimensions.paperHeight,
								 chartOrientation,
								 chartType, 
								 showChart, 
								 xtitle, 
								 ytitle, 
								 mdtSurvival.labels,
								 mdtSurvival.values, 
								 traces,
                                 null,
                                 styling)}
			{fullScreenChart}
            {(!showChart && activeFilters?.Facility?.value.length !== 1 && activeFilters?.PrimarySite?.value.length !== 1 ? 
                <div style={{marginTop: chartDimensions.middleChartText}}>Please select a single Facility and Cancer to generate data</div> : null)}
		</DashChart>
	 	</div>
	);
}
